/**
 * Other currencies (Nakasero Estates, Oct 2026): "the owners ask us in
 * dollars… our suppliers are from China or Europe, so we are charged in
 * yuan or dollars, and these rates keep changing".
 *
 * Money is KEPT in shillings everywhere — the ledger, the store, every order.
 * A brand lists the other currencies it works in, each with a rate (what ONE
 * unit costs in UGX) and the day the rate was set (`brandCurrencies`). That
 * rate turns shillings into dollars or yuan for a quotation, a BOQ, a bill, a
 * delivery bought abroad, the construction screens and the website, and the
 * documents print the rate they used.
 */

import { formatDayInt } from "./accommodation";

export const BASE_CURRENCY = "UGX";

export interface CurrencyInfo {
  code: string;
  name: string;
  /** Decimal places its money is written with (dollars have cents; shillings none). */
  decimals: number;
}

/** The currencies a brand can add, in the order the picker lists them. */
export const CURRENCIES: readonly CurrencyInfo[] = [
  { code: "USD", name: "US dollar", decimals: 2 },
  { code: "CNY", name: "Chinese yuan", decimals: 2 },
  { code: "EUR", name: "Euro", decimals: 2 },
  { code: "GBP", name: "British pound", decimals: 2 },
  { code: "AED", name: "UAE dirham", decimals: 2 },
  { code: "INR", name: "Indian rupee", decimals: 2 },
  { code: "ZAR", name: "South African rand", decimals: 2 },
  { code: "JPY", name: "Japanese yen", decimals: 0 },
  { code: "CAD", name: "Canadian dollar", decimals: 2 },
  { code: "AUD", name: "Australian dollar", decimals: 2 },
  { code: "KES", name: "Kenyan shilling", decimals: 0 },
  { code: "TZS", name: "Tanzanian shilling", decimals: 0 },
  { code: "RWF", name: "Rwandan franc", decimals: 0 },
  { code: "SSP", name: "South Sudanese pound", decimals: 0 },
  { code: "CDF", name: "Congolese franc", decimals: 0 },
];

/** A rate in use: the currency, UGX for one unit of it, and the day it was set. */
export interface Fx {
  code: string;
  rate: number;
  day?: number;
}

export function currencyInfo(code: string | undefined | null): CurrencyInfo | undefined {
  const c = String(code ?? "").toUpperCase();
  return CURRENCIES.find((x) => x.code === c);
}

export function isForeignCode(code: string | undefined | null): boolean {
  return !!currencyInfo(code);
}

export function currencyDecimals(code: string | undefined | null): number {
  if (!code || String(code).toUpperCase() === BASE_CURRENCY) return 0;
  return currencyInfo(code)?.decimals ?? 2;
}

function roundTo(n: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round((Number(n) || 0) * f) / f;
}

/** An amount in a currency, rounded the way that currency is written. */
export function roundIn(amount: number, code: string | undefined | null): number {
  return roundTo(amount, currencyDecimals(code));
}

/**
 * A rate as it can be kept: positive, sane, at most 4 decimal places (a
 * Tanzanian shilling is about 1.45 UGX). Null when it is not a rate.
 */
export function cleanRate(n: unknown): number | null {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0 || v > 10_000_000) return null;
  const r = roundTo(v, 4);
  return r > 0 ? r : null;
}

/** Shillings in the other currency at `rate` (UGX for one unit). */
export function fromBase(ugx: number, rate: number, code: string): number {
  if (!(rate > 0)) return 0;
  return roundIn((Number(ugx) || 0) / rate, code);
}

/** An amount in the other currency, in whole shillings at `rate`. */
export function toBase(amount: number, rate: number): number {
  if (!(rate > 0)) return 0;
  return Math.round((Number(amount) || 0) * rate);
}

/**
 * "USD 1,234.50", "USD 500,000", "UGX 4,567,000", "KES 12,000". Cents are
 * written only when there are some, so a round price reads as one.
 */
export function formatIn(amount: number, code: string | undefined | null): string {
  const c = String(code || BASE_CURRENCY).toUpperCase();
  const d = currencyDecimals(c);
  const v = roundTo(amount, d);
  const hasCents = d > 0 && Math.abs(v - Math.trunc(v)) > 1e-9;
  const text = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? d : 0,
    maximumFractionDigits: hasCents ? d : 0,
  });
  return `${v < 0 ? "-" : ""}${c} ${text}`;
}

/** A shilling amount written in `fx`'s currency — or in UGX when there is no rate. */
export function showIn(ugx: number, fx: Fx | null | undefined): string {
  if (!fx || !(fx.rate > 0) || fx.code === BASE_CURRENCY) return formatIn(Math.round(Number(ugx) || 0), BASE_CURRENCY);
  return formatIn(fromBase(ugx, fx.rate, fx.code), fx.code);
}

/** The number alone (no code) — a table column already headed "USD". */
export function numberIn(ugx: number, fx: Fx | null | undefined): string {
  const s = showIn(ugx, fx);
  return s.slice(s.indexOf(" ") + 1);
}

/** 635,800,000 UGX → "USD 171.8k"; the short form tiles use. */
export function shortIn(ugx: number, fx: Fx | null | undefined): string {
  const code = fx && fx.rate > 0 ? fx.code : BASE_CURRENCY;
  const v = fx && fx.rate > 0 ? (Number(ugx) || 0) / fx.rate : Number(ugx) || 0;
  const a = Math.abs(v);
  let n: string;
  if (a >= 1e9) n = `${(v / 1e9).toFixed(2).replace(/\.?0+$/, "")}B`;
  else if (a >= 1e6) n = `${(v / 1e6).toFixed(1)}M`;
  else if (a >= 1e3) n = `${(v / 1e3).toFixed(a >= 1e5 ? 0 : 1).replace(/\.0$/, "")}k`;
  else n = String(Math.round(v));
  return code === BASE_CURRENCY ? n : `${code} ${n}`;
}

/** "1 USD = UGX 3,700 · rate of 10 Oct 2026" — printed wherever a rate was used. */
export function rateLine(fx: Fx): string {
  const r = fx.rate >= 100 ? Math.round(fx.rate).toLocaleString("en-US") : String(roundTo(fx.rate, 4));
  return `1 ${fx.code} = UGX ${r}${fx.day ? ` · rate of ${formatDayInt(fx.day, true)}` : ""}`;
}

/** A brand's currency row, as the pages and documents receive it. */
export interface BrandCurrency {
  code: string;
  rate: number;
  rateDay: number;
  website?: "also" | "first";
}

/** The rate in use for `code` from a brand's list, or null (UGX, or not listed). */
export function fxFor(rows: readonly BrandCurrency[] | null | undefined, code: string | null | undefined): Fx | null {
  if (!code || code === BASE_CURRENCY) return null;
  const row = (rows ?? []).find((r) => r.code === code);
  return row && row.rate > 0 ? { code: row.code, rate: row.rate, day: row.rateDay } : null;
}

/** The currency the website shows prices in too, if the brand picked one. */
export function websiteCurrency(rows: readonly BrandCurrency[] | null | undefined): (Fx & { first: boolean }) | null {
  const row = (rows ?? []).find((r) => r.website === "also" || r.website === "first");
  return row && row.rate > 0 ? { code: row.code, rate: row.rate, day: row.rateDay, first: row.website === "first" } : null;
}

/**
 * The price a website shows beside the UGX one: the price the owner set in
 * that currency, else the UGX price at the website currency's rate.
 */
export function websitePrice(
  ugx: number,
  fixed: { code: string; amount: number } | null | undefined,
  site: (Fx & { first: boolean }) | null | undefined,
): { code: string; amount: number; first: boolean } | null {
  if (fixed && fixed.amount > 0 && isForeignCode(fixed.code)) {
    return { code: fixed.code, amount: roundIn(fixed.amount, fixed.code), first: !!site?.first && site.code === fixed.code };
  }
  if (site && ugx > 0) return { code: site.code, amount: fromBase(ugx, site.rate, site.code), first: site.first };
  return null;
}

/**
 * What a product cost in the currency it was bought in (Luckiestar Tyres, 22
 * Oct 2026: "some of us buy in dollars or Chinese yuan … the rates keep
 * changing"): the amount, the rate paid (UGX for one unit — the shop's own,
 * or its saved rate) and the day it was typed. The cost price stays in UGX
 * (amount × rate); this is kept beside it so an edit shows where it came from.
 */
export interface CostFx {
  code: string;
  amount: number;
  rate: number;
  day?: number;
}

/** A bought-abroad cost, cleaned for storing — or undefined (UGX, or not a real amount and rate). */
export function cleanCostFx(x: { code?: string; amount?: number; rate?: number; day?: number } | null | undefined): CostFx | undefined {
  if (!x || !isForeignCode(x.code)) return undefined;
  const code = String(x.code).toUpperCase();
  const amount = roundIn(Number(x.amount) || 0, code);
  const rate = cleanRate(x.rate);
  if (!(amount > 0) || rate === null) return undefined;
  return { code, amount, rate, ...(typeof x.day === "number" && x.day > 0 ? { day: Math.round(x.day) } : {}) };
}

/** A fixed price in another currency, cleaned for storing — or undefined. */
export function cleanForeignPrice(p: { code?: string; amount?: number } | null | undefined): { code: string; amount: number } | undefined {
  if (!p || !isForeignCode(p.code)) return undefined;
  const amount = roundIn(Number(p.amount) || 0, p.code!);
  return amount > 0 ? { code: String(p.code).toUpperCase(), amount } : undefined;
}
